'use client';

import { useToast } from '@common/hooks';
import { useSearchParams } from 'next/navigation';
import { useEffect, useSyncExternalStore } from 'react';
import { AuthClient } from '@/api';
import { useAccessToken } from '@/hooks/useAccessToken';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { cookies } from '@/utils/cookies';
import { formatTestChannel, parseTestChannel, TEST_CHANNEL_COOKIE, type TestChannel } from '@/utils/testChannel';
const TEST_CHANNEL_PARAM = 'testChannel';
const TEST_MEMBER_PARAM = 'testMemberId';
const TEST_CHANNEL_OFF = 'OFF';
//* local 백엔드에만 있는 API 라 생성 타입에 기대지 않는다
const TEST_LOGIN_PATH = '/wello/v2/allows/test-login';
type TestLoginResponse = {
  context: {
    accessToken: string;
    refreshToken: string;
  };
};
const requestTestLogin = async (memberOAuthId: number, entry: TestChannel) => {
  const {
    data
  } = await AuthClient.post<{
    200: TestLoginResponse;
  }, unknown, true>({
    url: TEST_LOGIN_PATH,
    body: {
      memberOAuthId,
      entry
    },
    headers: {
      'Content-Type': 'application/json'
    },
    throwOnError: true
  });
  return data.context;
};

//* 서버 렌더·부트스트랩 스크립트가 쿠키로 채널을 다시 읽어야 해서 쿼리를 걷고 문서째 새로 연다
const reloadWithoutTestParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete(TEST_CHANNEL_PARAM);
  url.searchParams.delete(TEST_MEMBER_PARAM);
  window.location.replace(url.toString());
};
const subscribeNothing = () => () => {};
export const TestChannelController = () => {
  const searchParams = useSearchParams();
  const {
    setAccessToken,
    setRefreshToken
  } = useAccessToken();
  const {
    toastError
  } = useToast();
  const setWebviewType = useDeviceInfoStore(state => state.setWebviewType);
  const testChannelCookie = useSyncExternalStore(subscribeNothing, () => cookies.get<string | undefined>(TEST_CHANNEL_COOKIE), () => undefined);
  const activeTestChannel = parseTestChannel(testChannelCookie);
  const requestedChannel = searchParams.get(TEST_CHANNEL_PARAM);
  const requestedMemberId = searchParams.get(TEST_MEMBER_PARAM);
  useEffect(() => {
    if (activeTestChannel?.channel === 'TOSS') {
      setWebviewType(WEBVIEW_TYPE.TOSS);
    }
  }, [activeTestChannel?.channel, setWebviewType]);
  useEffect(() => {
    if (!requestedChannel) {
      return;
    }
    if (requestedChannel === TEST_CHANNEL_OFF) {
      cookies.remove(TEST_CHANNEL_COOKIE, {
        path: '/'
      });
      reloadWithoutTestParams();
      return;
    }
    const requested = parseTestChannel(`${requestedChannel}.${requestedMemberId}`);
    if (!requested) {
      toastError({
        error: new Error('testChannel=WELLO|KB_PAY|KAKAO_BANK|TOSS 와 숫자 testMemberId 가 필요해요')
      });
      return;
    }

    //* StrictMode 이중 실행의 앞선 응답은 버린다 — 로그인마다 refresh token 이 바뀌면 죽은 토큰이 남는다
    let isStale = false;
    requestTestLogin(requested.memberOAuthId, requested.channel).then(({
      accessToken,
      refreshToken
    }) => {
      if (isStale) {
        return;
      }
      setAccessToken(accessToken);
      setRefreshToken(refreshToken);
      cookies.set(TEST_CHANNEL_COOKIE, formatTestChannel(requested), {
        path: '/'
      });
      reloadWithoutTestParams();
    }).catch((error: unknown) => {
      if (!isStale) {
        toastError({
          error
        });
      }
    });
    return () => {
      isStale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 쿼리가 바뀔 때만 한 번 처리한다
  }, [requestedChannel, requestedMemberId]);
  if (!activeTestChannel) {
    return null;
  }
  return <p className="pointer-events-none fixed bottom-10 right-10 z-50 rounded-pill bg-R-500 px-12 py-4 font-700 text-Gray-white opacity-80 typo-caption1">
      TEST CHANNEL: {activeTestChannel.channel} / member{' '}
      {activeTestChannel.memberOAuthId}
    </p>;
};